import React, { useCallback, useEffect, useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import { useApi } from "../../hooks/useApi";
import { HelpDot } from "./FusionBits";
import { consequenceHeadline } from "./FusionConsequence";
import FusionEmptyState from "./FusionEmptyState";
import {
  SORTS, SEVERITIES, CONSEQUENCES, NON_ADDITIVE_TEXT, BREAK_POINT, fetchChains, formatMoney, laneLabel,
  severityClass,
} from "./fusionApi";

const PAGE = 10;

const titleCase = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : s);

/**
 * The chain list for one scope (FUSION.md §7), optionally narrowed to a heatmap cell (§7.1). Every
 * filter is "contains at least one X", so it never changes how chains were deduplicated (A6).
 */
const FusionScopeView = ({ scope, bucket, bucketSeverity, onClearBucket, onOpen, refreshNonce, orgChains, entityLinks, coverage, coverageAvailable, onOpenCoverage, initialConsequence }) => {
  const apiCall = useApi();
  const [sort, setSort] = useState("severity");
  const [severity, setSeverity] = useState("");
  // Seeded from a /Fusion?consequence= deep link, already checked against CONSEQUENCES by FusionPage.
  const [consequence, setConsequence] = useState(initialConsequence || "");
  const [data, setData] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  // Only the newest request may write: a slow earlier answer must not replace a newer sort or filter.
  const latest = useRef(0);
  const seenNonce = useRef(refreshNonce);

  const query = useCallback((skip, refresh = false) => fetchChains(apiCall, {
    scope,
    bucket: bucket || undefined,
    // A heatmap cell pins the severity to that cell; otherwise the dropdown applies.
    severity: bucket ? titleCase(bucketSeverity) : severity || undefined,
    consequence: consequence || undefined,
    sort,
    skip,
    take: PAGE,
    refresh,
  }), [apiCall, scope, bucket, bucketSeverity, severity, consequence, sort]);

  // A new scope, cell, sort or filter starts again from the first page.
  const load = useCallback(async ({ refresh = false } = {}) => {
    const id = ++latest.current;
    setLoading(true);
    setFailed(false);
    try {
      const page = await query(0, refresh);
      if (id !== latest.current) return;
      setData(page);
      setItems(page?.items || []);
    } catch {
      if (id === latest.current) setFailed(true);
    } finally {
      if (id === latest.current) setLoading(false);
    }
  }, [query]);

  // A refreshNonce re-read (after Refresh or an action) goes with refresh=true, so whichever server
  // answers rebuilds at most once instead of serving a list built before the action.
  useEffect(() => {
    const refresh = refreshNonce !== seenNonce.current;
    seenNonce.current = refreshNonce;
    load({ refresh });
  }, [load, refreshNonce]);

  const loadMore = async () => {
    const id = ++latest.current;
    setLoading(true);
    try {
      const page = await query(items.length);
      if (id !== latest.current) return;
      setData(page);
      setItems((prev) => [...prev, ...(page?.items || [])]);
    } catch {
      if (id === latest.current) setFailed(true);
    } finally {
      if (id === latest.current) setLoading(false);
    }
  };

  const maxExposure = Math.max(0, ...items.map((x) => Number(x.connectedExposureMean) || 0));

  return (
    <div>
      {bucket && (
        <div className="flex items-center gap-2 flex-wrap rounded-lg border border-violet-200 bg-violet-50 px-3 py-2 mb-3 text-sm text-violet-900">
          <span>Organization · from heatmap:</span>
          <b>{bucket} · {titleCase(bucketSeverity)}</b>
          <button onClick={onClearBucket} className="ml-auto text-xs px-2 py-0.5 rounded border border-violet-300 hover:bg-violet-100">Clear</button>
        </div>
      )}

      <div className="flex items-center gap-1.5 flex-wrap mb-3">
        <span className="text-xs text-content-muted">Sort</span>
        {SORTS.map((s) => (
          <button
            key={s.key}
            onClick={() => setSort(s.key)}
            className={`text-xs px-2.5 py-1 rounded-lg border ${sort === s.key ? "bg-indigo-600 text-white border-indigo-600" : "bg-theme-secondary text-content-secondary border-border"}`}
          >
            {s.label}
          </button>
        ))}
        {!bucket && (
          <select
            aria-label="Severity filter"
            value={severity}
            onChange={(e) => setSeverity(e.target.value)}
            className="text-xs px-2 py-1 rounded-lg border border-border bg-theme-secondary text-content-secondary"
          >
            <option value="">Any severity</option>
            {/* "Contains": a chain matches on any one of its risks (plan A6), not on its worst. */}
            {SEVERITIES.map((s) => <option key={s} value={s}>Contains {s}</option>)}
          </select>
        )}
        {/* Also "contains": a chain matches when any of its consequence statements is in the category. */}
        <select
          aria-label="Consequence filter"
          value={consequence}
          onChange={(e) => setConsequence(e.target.value)}
          className="text-xs px-2 py-1 rounded-lg border border-border bg-theme-secondary text-content-secondary"
        >
          <option value="">Any consequence</option>
          {CONSEQUENCES.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
        </select>
        {loading && data && (
          <span className="inline-flex items-center gap-1 text-xs text-indigo-600" role="status">
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Updating…
          </span>
        )}
        {data && (
          <span className="ml-auto text-xs text-content-secondary flex items-center gap-1">
            Scope exposure <b className="text-content-primary">{formatMoney(data.scopeExposure?.mean)}</b>
            <HelpDot text={`${NON_ADDITIVE_TEXT} Shared risks are counted once in this figure.`} />
          </span>
        )}
      </div>
      {!bucket && severity && (
        <p className="-mt-2 mb-3 text-xs text-content-muted">
          Chains with at least one {severity} risk. Each chain's badge shows its worst risk.
        </p>
      )}

      {loading && !data && (
        <div className="py-6 flex items-center justify-center gap-2 text-sm text-content-muted" role="status">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading chains…
        </div>
      )}
      {failed && <div className="py-6 text-center text-sm text-content-muted">Chains could not be loaded. <button className="text-indigo-600 hover:underline" onClick={load}>Try again</button></div>}

      {/* Org-wide empty vs this view empty is decided by the summary's chain count, never by the list
          (FUSION.md §7.2). The Partial view banner above already covers a restricted user. */}
      {data && items.length === 0 && (
        orgChains === 0
          ? <FusionEmptyState variant="org" entityLinks={entityLinks} coverage={coverage}
              coverageAvailable={coverageAvailable} onOpenCoverage={onOpenCoverage} />
          : <FusionEmptyState variant="view" />
      )}

      {/* While a new sort, filter or scope loads, the old list is dimmed so it is never read as the answer. */}
      <div className={loading && data ? "opacity-50 pointer-events-none transition-opacity" : "transition-opacity"} aria-busy={loading}>
      {items.map((item, i) => (
        <div
          key={`${item.anchorType}:${item.anchorId}`}
          role="button"
          tabIndex={0}
          onClick={() => onOpen(item.anchorType, item.anchorId)}
          onKeyDown={(e) => { if (e.key === "Enter") onOpen(item.anchorType, item.anchorId); }}
          className="flex gap-2.5 items-start rounded-lg border border-border bg-theme-secondary px-3 py-2.5 mb-2 cursor-pointer hover:bg-theme-hover"
        >
          <span className="text-xs text-content-muted min-w-[16px] text-right pt-0.5">{i + 1}</span>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold text-content-primary">{item.anchorLabel}</span>
              <span className={`text-[10px] px-2 py-0.5 rounded font-semibold ${severityClass(item.highestSeverity)}`}>{item.highestSeverity}</span>
              {item.truncated && <span className="text-xs text-content-muted">more connected objects exist beyond this view</span>}
            </div>
            {/* The sample's path: one module per hop; a hop opens this row's chain with that step highlighted. */}
            <div className="text-[13px] mt-1 flex flex-wrap items-center gap-x-1">
              {item.path.map((p, idx) => (
                <React.Fragment key={idx}>
                  {idx > 0 && <span className="text-content-muted">→</span>}
                  <button
                    onClick={(e) => { e.stopPropagation(); onOpen(item.anchorType, item.anchorId, { focus: { distance: p.distance, lane: p.lane } }); }}
                    title={`Show this chain with ${p.firstLabel}${p.members > 1 ? ` and ${p.members - 1} more` : ""} highlighted`}
                    className="text-indigo-600 hover:underline"
                  >
                    {laneLabel(p.lane)}{p.members > 1 ? ` ×${p.members}` : ""}
                  </button>
                </React.Fragment>
              ))}
              {/* The path runs to its end: the chain's derived consequence, else the risks' own forecast. */}
              <span className="text-content-muted">→</span>
              {item.consequenceHeadline ? (
                <span className="text-red-700 font-medium">{item.consequenceHeadline}</span>
              ) : (
                <span className="text-red-700 font-medium" title={item.consequence?.whatWillBreak?.[0] || ""}>
                  If not attended: {consequenceHeadline(item.consequence) || "no forecast recorded"}
                </span>
              )}
            </div>
            {!item.consequenceHeadline && item.consequence?.whatWillBreak?.[0] && (
              <div className="text-[11px] text-red-800/80 mt-0.5 truncate" title={item.consequence.whatWillBreak[0]}>
                What will break: {item.consequence.whatWillBreak[0]}
              </div>
            )}
            <div className="text-[11px] text-content-muted mt-1">
              {item.owners} team{item.owners === 1 ? "" : "s"} · {item.confirmedLinkSharePct}% confirmed · breaks {item.highestLeverage} chain{item.highestLeverage === 1 ? "" : "s"}
            </div>
            {item.breakPoint && (
              <div className="text-[11px] text-violet-700 mt-0.5">
                ◆ {BREAK_POINT}: {item.breakPoint.point?.label} · removes {formatMoney(item.breakPoint.meanRemovedAcrossChains)} across {item.breakPoint.chainCount} chain{item.breakPoint.chainCount === 1 ? "" : "s"}
              </div>
            )}
            <div className="h-1.5 rounded bg-border overflow-hidden mt-1.5 max-w-[280px]">
              <span className="block h-full bg-indigo-500" style={{ width: `${maxExposure > 0 ? Math.round((item.connectedExposureMean / maxExposure) * 100) : 0}%` }} />
            </div>
          </div>
          <div className="min-w-[56px] text-right font-semibold text-content-primary pt-0.5 whitespace-nowrap">{formatMoney(item.connectedExposureMean)}</div>
          <span className="text-content-muted pt-0.5">›</span>
        </div>
      ))}

      </div>

      {data && (
        <div className="flex items-center gap-3 mt-2 text-xs text-content-muted">
          <span>Showing {items.length} of {data.total} chains · sorted by {(SORTS.find((x) => x.key === sort)?.label || sort).toLowerCase()} · click a chain or a module in its path</span>
          {items.length < data.total && (
            <button onClick={loadMore} disabled={loading}
              className="px-2.5 py-1 rounded-lg border border-border text-content-secondary hover:border-indigo-200">
              Show more
            </button>
          )}
          <span className="ml-auto">{NON_ADDITIVE_TEXT}</span>
        </div>
      )}
    </div>
  );
};

export default FusionScopeView;
